/**
 * =============================================================================
 * IDS CRM Web - Customer Create/Edit Form
 * =============================================================================
 *
 * PURPOSE:
 * Form for creating and editing customer records.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { useAuth, API_URL } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import CountrySelect from '../components/CountrySelect';
import { CUSTOMER_STATUS_ORDER, CUSTOMER_STATUS_META, TYPE_FOR_STATUS, initialStatusFromParams } from '../utils/customerStatus';
import './CustomerForm.css';

// A customer and a distributor share these pages, and are opened from the End
// Customers list and from Distributors: "←" goes back to the list the page
// was opened from, as that list was left.
const CUSTOMER_LISTS = { '/customers': 'Customers', '/distributors': 'Distributors' };

function CustomerForm() {
  const { guardElement, markDirty } = useUnsavedChanges();
  const errorBannerRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authFetch } = useAuth();
  const logoInputRef = useRef(null);
  const isEdit = Boolean(id);
  // Users set Status only; an end customer's type follows it (Gareth
  // 2026-09-14). A new form always starts on a matching pair: ?status= from the
  // End Customers list, or the older ?type= links (Distributors, New
  // Opportunity). A new distributor still starts Active.
  const requestedType = searchParams.get('type');
  const requestedStatus = searchParams.get('status');
  const initialStatus = initialStatusFromParams(requestedStatus, requestedType);
  const initialCustomerType = requestedType === 'distributor' ? 'distributor' : TYPE_FOR_STATUS[initialStatus];
  // Once Status has been chosen by hand, picking Distributor leaves it alone.
  const statusTouched = useRef(false);
  // The status a new form had before picking Distributor set Active, so going
  // back to End customer puts it back: a Prospect stays a Prospect.
  const statusBeforeDistributor = useRef(null);
  // The distributor links the record loaded with. The pickers find Active
  // distributors only, so a cleared link to a prospective or dormant one could
  // not be searched for again; Restore puts it back.
  const storedDistributorLinks = useRef({});

  // Round-trip support: another form (e.g. New Opportunity) can send the user
  // here to create a customer and get them back with the new record selected.
  // Internal app paths only — never an absolute/protocol-relative URL.
  const rawReturnTo = searchParams.get('return_to');
  const returnTo = rawReturnTo && rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//')
    ? rawReturnTo
    : null;

  const navigateBackWith = (customerId, customerType) => {
    const param = customerType === 'distributor' ? 'distributor_id' : 'customer_id';
    const separator = returnTo.includes('?') ? '&' : '?';
    navigate(`${returnTo}${separator}${param}=${customerId}`);
  };

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [pendingLogo, setPendingLogo] = useState(null);
  const [accountOwners, setAccountOwners] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([
    { currency_code: 'GBP', currency_name: 'British Pound' },
    { currency_code: 'EUR', currency_name: 'Euro' },
    { currency_code: 'USD', currency_name: 'US Dollar' }
  ]);
  const [duplicateCustomer, setDuplicateCustomer] = useState(null);
  const [staleRecord, setStaleRecord] = useState(false);
  const [farmSites, setFarmSites] = useState([]);
  // Seeds for the distributor typeahead pickers (edit mode / cross-field syncs).
  const [distributorLabels, setDistributorLabels] = useState({ master: '', sub: '', install: '', brush: '' });
  // Primary contact captured on CREATE only — the record's Contacts tab
  // manages the full list afterwards (Gareth 2026-08-04).
  const [primaryContact, setPrimaryContact] = useState({ name: '', phone: '', email: '' });
  // Site facts live only on the site form (Gareth 2026-08-21). This flag just
  // decides whether the site form starts pre-filled with the business address.
  const [siteSameAsBusiness, setSiteSameAsBusiness] = useState(true);
  // Set after a farm customer is created: shows the forced "continue to the
  // site form" popup, which is the only way onwards.
  const [createdCustomerId, setCreatedCustomerId] = useState(null);

  const [formData, setFormData] = useState({
    updated_at: '',
    business_name: '',
    legal_name: '',
    customer_type: initialCustomerType,
    status: initialStatus,
    tier: 'standard',
    address_line_1: '',
    address_line_2: '',
    town_city: '',
    county_region: '',
    state_province: '',
    postcode: '',
    country_code: 'GB',
    currency: 'GBP',
    payment_terms: 'net_30',
    account_owner_id: '',
    source: '',
    source_other: '',
    sale_channel: 'direct',
    commercial_owner: 'ptl',
    master_distributor_id: '',
    sub_distributor_id: '',
    install_distributor_id: '',
    brush_supply_channel: 'direct',
    brush_purchase_cadence: 'as_required',
    brush_unit_price: '',
    brush_last_ship_date: '',
    brush_distributor_id: '',
    brush_distributor_price: '',
    brush_ordering_agreement: '',
    health_score: 'green',
    notes: '',
    logo_url: ''
  });

  // A failure set below the fold is useless if the user can't see it.
  useEffect(() => {
    if (error && errorBannerRef.current) {
      errorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  useEffect(() => {
    const fetchAccountOwners = async () => {
      try {
        const [ownersResponse, currencyResponse] = await Promise.all([
          authFetch('/api/v1/customers/account-owners'),
          authFetch('/api/v1/admin/currency-rates')
        ]);
        if (ownersResponse.ok) {
          const data = await ownersResponse.json();
          setAccountOwners(data.data || []);
        }
        if (currencyResponse.ok) {
          const data = await currencyResponse.json();
          setCurrencyOptions(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load form options:', err);
      }
    };

    fetchAccountOwners();
  }, [authFetch]);

  useEffect(() => {
    if (isEdit) {
      const fetchCustomer = async () => {
        try {
          const response = await authFetch(`/api/v1/customers/${id}`);
          if (response.ok) {
            const data = await response.json();
            setFormData({
              updated_at: data.updated_at || '',
              business_name: data.business_name || '',
              legal_name: data.legal_name || '',
              customer_type: data.customer_type || 'prospect',
              status: data.status || 'prospect',
              tier: data.tier || 'standard',
              address_line_1: data.address_line_1 || '',
              address_line_2: data.address_line_2 || '',
              town_city: data.town_city || '',
              county_region: data.county_region || '',
              state_province: data.state_province || '',
              postcode: data.postcode || '',
              country_code: data.country_code || 'GB',
              currency: data.currency || 'GBP',
              payment_terms: data.payment_terms || 'net_30',
              account_owner_id: data.account_owner_id || '',
              source: data.source || '',
              source_other: data.source_other || '',
              sale_channel: data.sale_channel || 'direct',
              commercial_owner: data.commercial_owner || 'ptl',
              master_distributor_id: data.master_distributor_id || '',
              sub_distributor_id: data.sub_distributor_id || '',
              install_distributor_id: data.install_distributor_id || '',
              brush_supply_channel: data.brush_supply_channel || 'direct',
              brush_purchase_cadence: data.brush_purchase_cadence || 'as_required',
              brush_unit_price: data.brush_unit_price_minor_units ? String((Number(data.brush_unit_price_minor_units) / 100).toFixed(2)) : '',
              brush_last_ship_date: data.brush_last_ship_date ? data.brush_last_ship_date.split('T')[0] : '',
              brush_distributor_id: data.brush_distributor_id || '',
              brush_distributor_price: data.brush_distributor_price_minor_units ? String((Number(data.brush_distributor_price_minor_units) / 100).toFixed(2)) : '',
              brush_ordering_agreement: data.brush_ordering_agreement || '',
              health_score: data.health_score || 'green',
              notes: data.notes || '',
              logo_url: data.logo_url || ''
            });
            setDistributorLabels({
              master: data.master_distributor_name || '',
              sub: data.sub_distributor_name || '',
              install: data.install_distributor_name || '',
              brush: data.brush_distributor_name || ''
            });
            storedDistributorLinks.current = {
              master: { id: data.master_distributor_id || '', label: data.master_distributor_name || '' },
              sub: { id: data.sub_distributor_id || '', label: data.sub_distributor_name || '' },
              install: { id: data.install_distributor_id || '', label: data.install_distributor_name || '' },
              brush: { id: data.brush_distributor_id || '', label: data.brush_distributor_name || '' }
            };

            // Sites feed the read-only manager list only — site facts are
            // edited on the site form, never here (Gareth 2026-08-21).
            if (data.customer_type !== 'distributor') {
              const sitesResponse = await authFetch(`/api/v1/customers/${id}/sites`);
              if (sitesResponse.ok) {
                const sitesData = await sitesResponse.json();
                setFarmSites(sitesData.data || []);
              } else {
                throw new Error('Failed to load farm sites');
              }
            } else {
              setFarmSites([]);
            }
          } else {
            setError('Customer not found');
          }
        } catch (err) {
          setError(err.message || 'Failed to load customer');
        } finally {
          setLoading(false);
        }
      };
      fetchCustomer();
    }
  }, [id, isEdit, authFetch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (['business_name', 'postcode', 'town_city', 'country_code'].includes(name)) {
      setDuplicateCustomer(null);
    }
    if (name === 'status') statusTouched.current = true;
    // A new distributor starts Active unless Status was already chosen; the
    // status it replaced comes back if End customer is picked again first.
    const startsActive = name === 'customer_type' && value === 'distributor' && !isEdit && !statusTouched.current;
    if (startsActive) statusBeforeDistributor.current = formData.status;
    const restoredStatus = name === 'customer_type' && value !== 'distributor' && !statusTouched.current
      ? statusBeforeDistributor.current
      : null;
    if (name === 'customer_type' && value !== 'distributor') statusBeforeDistributor.current = null;
    setFormData(prev => {
      // Status drives type for an end customer, so every save sends a matching
      // pair; a distributor keeps its type whatever its status.
      if (name === 'status') {
        return {
          ...prev,
          status: value,
          customer_type: prev.customer_type === 'distributor'
            ? 'distributor'
            : (TYPE_FOR_STATUS[value] || prev.customer_type)
        };
      }

      // A new distributor starts Active, as it always has, unless Status was
      // already chosen. The End customer option carries its own matching type.
      if (startsActive) {
        return { ...prev, customer_type: value, status: 'active' };
      }

      // Back to End customer before Status was touched: the status it had
      // before Distributor, with the type that status carries.
      if (restoredStatus) {
        return { ...prev, customer_type: TYPE_FOR_STATUS[restoredStatus], status: restoredStatus };
      }

      if (name === 'sale_channel' && value === 'direct') {
        return {
          ...prev,
          sale_channel: value,
          commercial_owner: 'ptl',
          master_distributor_id: '',
          sub_distributor_id: '',
          install_distributor_id: ''
        };
      }

      if (name === 'sale_channel' && value !== 'direct') {
        return {
          ...prev,
          sale_channel: value,
          commercial_owner: 'distributor'
        };
      }

      return { ...prev, [name]: value };
    });
    if (name === 'sale_channel' && value === 'direct') {
      setDistributorLabels(prev => ({ ...prev, master: '', sub: '', install: '' }));
    }
  };

  const handleDistributorSelect = (field) => (row) => {
    markDirty();
    const value = row ? row.customer_id : '';
    const label = row ? row.business_name : '';

    if (field === 'master') {
      // A master pick that matches the current sub clears the sub; install
      // silently follows the master unless the channel is install-only.
      const subCollides = Boolean(value) && value === formData.sub_distributor_id;
      setFormData(prev => ({
        ...prev,
        master_distributor_id: value,
        sub_distributor_id: subCollides ? '' : prev.sub_distributor_id,
        install_distributor_id: prev.sale_channel === 'distributor_install_only' ? prev.install_distributor_id : value
      }));
      setDistributorLabels(prev => ({
        ...prev,
        master: label,
        sub: subCollides ? '' : prev.sub
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [`${field}_distributor_id`]: value }));
    setDistributorLabels(prev => ({ ...prev, [field]: label }));
  };

  // A stored link that has been cleared (typing in a picker clears it) gets a
  // Restore button: existing links to prospective or dormant distributors stay
  // (Gareth 2026-09-14), but the Active-only search can't find them again.
  const renderRestoreDistributor = (field) => {
    const stored = storedDistributorLinks.current[field];
    if (!stored?.id || formData[`${field}_distributor_id`]) return null;
    return (
      <button
        type="button"
        className="btn-link"
        onClick={() => handleDistributorSelect(field)({ customer_id: stored.id, business_name: stored.label })}
      >
        Restore {stored.label || 'the saved distributor'}
      </button>
    );
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const uploadLogoForCustomer = async (customerId, logoData) => {
    const response = await authFetch(`/api/v1/customers/${customerId}/logo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logo: logoData })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to upload logo');
    }

    return response.json();
  };

  // The only exit from the post-create popup: onwards to the site form, with
  // the business address carried over when the flag says the site lives there.
  // return_to rides along so mid-flow creations (e.g. New Opportunity) come
  // back to their caller after the site is saved, not instead of it.
  const continueToSiteForm = () => {
    const params = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : '';
    navigate(`/customers/${createdCustomerId}/sites/new${params}`, {
      state: { prefillAddress: siteSameAsBusiness, fromCustomerCreate: true }
    });
  };

  const formatSiteAddress = (site) => [
    site.address_line_1,
    site.address_line_2,
    site.town_city,
    site.county_region,
    site.state_province,
    site.postcode
  ].filter(Boolean).join(', ');

  const handleLogoClick = () => {
    logoInputRef.current?.click();
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const logoData = reader.result;
      setLogoPreview(logoData);
      setPendingLogo(logoData);
      setError(null);

      if (!isEdit) {
        return;
      }

      setUploadingLogo(true);
      try {
        const data = await uploadLogoForCustomer(id, logoData);
        setFormData(prev => ({ ...prev, logo_url: data.logo_url || '' }));
        setPendingLogo(null);
      } catch (err) {
        setError(err.message || 'Failed to upload logo');
      } finally {
        setUploadingLogo(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveLogo = async () => {
    setLogoPreview(null);
    setPendingLogo(null);

    if (!isEdit || !formData.logo_url) {
      setFormData(prev => ({ ...prev, logo_url: '' }));
      return;
    }

    setUploadingLogo(true);
    try {
      const response = await authFetch(`/api/v1/customers/${id}/logo`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setFormData(prev => ({ ...prev, logo_url: '' }));
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to remove logo');
      }
    } catch (err) {
      setError('Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setDuplicateCustomer(null);
    setStaleRecord(false);

    try {
      // Name the missing required fields instead of failing generically
      // (also catches whitespace-only values the HTML required check allows).
      const missingFields = [];
      if (!formData.business_name.trim()) missingFields.push('Business Name');
      if (!formData.customer_type) missingFields.push('Type');
      // A record still on a retired status (e.g. At Risk) must pick one of the three.
      if (!CUSTOMER_STATUS_ORDER.includes(formData.status)) missingFields.push('Status');
      if (missingFields.length > 0) {
        throw new Error(`Please complete the required field${missingFields.length > 1 ? 's' : ''}: ${missingFields.join(', ')}`);
      }

      const url = isEdit ? `/api/v1/customers/${id}` : '/api/v1/customers';
      const method = isEdit ? 'PUT' : 'POST';

      // Clean up empty strings - PostgreSQL enums don't accept empty strings
      const cleanData = { ...formData };

      if (cleanData.customer_type !== 'distributor' && cleanData.sale_channel !== 'direct' && !cleanData.master_distributor_id) {
        throw new Error('Select the distributor that owns this indirect customer relationship.');
      }

      if (cleanData.sale_channel === 'direct') {
        cleanData.commercial_owner = 'ptl';
        cleanData.master_distributor_id = null;
        cleanData.sub_distributor_id = null;
        cleanData.install_distributor_id = null;
      } else {
        cleanData.commercial_owner = 'distributor';
        cleanData.master_distributor_id = cleanData.master_distributor_id || null;
        cleanData.sub_distributor_id = cleanData.sub_distributor_id || null;
        cleanData.install_distributor_id = cleanData.install_distributor_id || cleanData.master_distributor_id;
      }

      // Convert empty strings to null for optional enum fields
      if (cleanData.source !== 'other') cleanData.source_other = null;

      const enumFields = ['service_contract_type', 'source'];
      enumFields.forEach(field => {
        if (cleanData[field] === '') {
          cleanData[field] = null;
        }
      });

      if (cleanData.account_owner_id === '') {
        cleanData.account_owner_id = null;
      }

      // An end customer's health is worked out by the system: the form never
      // sends the value it happened to load, which may be out of date by now.
      if (cleanData.customer_type !== 'distributor') delete cleanData.health_score;

      cleanData.brush_unit_price_minor_units = cleanData.brush_unit_price === ''
        ? null
        : Math.round(Number(cleanData.brush_unit_price) * 100);
      cleanData.brush_distributor_price_minor_units = cleanData.brush_distributor_price === ''
        ? null
        : Math.round(Number(cleanData.brush_distributor_price) * 100);
      cleanData.brush_last_ship_date = cleanData.brush_last_ship_date || null;
      cleanData.brush_distributor_id = cleanData.brush_distributor_id || null;
      delete cleanData.brush_unit_price;
      delete cleanData.brush_distributor_price;

      // N/A brush supply: the dependent fields are greyed out - wipe them so
      // stale values don't linger behind the disabled inputs.
      if (cleanData.brush_supply_channel === 'none') {
        cleanData.brush_purchase_cadence = null;
        cleanData.brush_unit_price_minor_units = null;
        cleanData.brush_last_ship_date = null;
        cleanData.brush_distributor_id = null;
      }

      const response = await authFetch(url, {
        method,
        body: JSON.stringify(cleanData)
      });

      if (response.ok) {
        const data = await response.json();

        // Upload logo if pending
        if (!isEdit && pendingLogo) {
          try {
            await uploadLogoForCustomer(data.customer_id, pendingLogo);
          } catch (logoErr) {
            setError(`Customer saved, but logo upload failed: ${logoErr.message}`);
            setSaving(false);
            return;
          }
        }

        // Primary contact (create only, optional): first entry on the master
        // contact list — marked primary. Farm customers get their site created
        // NEXT (on the site form), so the contact starts unplaced and can be
        // placed from the site card afterwards; distributors place it at the
        // auto-created head-office depot. force=true: a new organisation
        // legitimately gets its own record; cross-org duplicates are handled
        // by the Contacts tab's link flow.
        if (!isEdit && primaryContact.name.trim()) {
          try {
            let placement = {};
            if (data.customer_type === 'distributor') {
              const depotsResponse = await authFetch(`/api/v1/distributors/${data.customer_id}/depots`);
              if (depotsResponse.ok) {
                const depotsData = await depotsResponse.json();
                const headOffice = (depotsData.data || []).find(depot => depot.is_head_office);
                if (headOffice) placement = { depot_id: headOffice.depot_id };
              }
            }

            const contactResponse = await authFetch('/api/v1/contacts', {
              method: 'POST',
              body: JSON.stringify({
                customer_id: data.customer_id,
                full_name: primaryContact.name.trim(),
                phone: primaryContact.phone.trim() || null,
                email: primaryContact.email.trim() || null,
                is_primary: true,
                force: true,
                ...placement
              })
            });
            const contactData = await contactResponse.json();
            if (!contactResponse.ok) {
              throw new Error(contactData.message || 'Failed to create the contact');
            }

            await authFetch(`/api/v1/customers/${data.customer_id}`, {
              method: 'PUT',
              body: JSON.stringify({ primary_contact_id: contactData.contact_id })
            });
          } catch (contactErr) {
            setError(`Customer saved, but the primary contact failed: ${contactErr.message}`);
            setSaving(false);
            return;
          }
        }

        // Farm customers: site facts are recorded on the site form, so a new
        // customer funnels straight into it via the forced popup (Gareth
        // 2026-08-21) — even mid-opportunity, or salespeople mint customers
        // with no site. Distributors and edits navigate as before.
        if (!isEdit && data.customer_type !== 'distributor') {
          setCreatedCustomerId(data.customer_id);
        } else if (returnTo && !isEdit) {
          navigateBackWith(data.customer_id, data.customer_type);
        } else {
          navigate(`/customers/${data.customer_id}`);
        }
      } else {
        const errorData = await response.json();
        if (response.status === 409 && errorData.existing_customer) {
          setDuplicateCustomer(errorData.existing_customer);
        } else if (response.status === 409) {
          setStaleRecord(true);
        }
        setError(errorData.message || 'Failed to save customer');
      }
    } catch (err) {
      setError(err.message || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-page">Loading...</div>;
  }

  // Show farm site section for all non-distributor types (customer, prospect, former)
  const showPrimarySiteSection = formData.customer_type !== 'distributor';
  // The Type box's End customer option: the record's own type, or for a
  // distributor being switched back, the type its current status gives.
  const endCustomerTypeValue = formData.customer_type !== 'distributor'
    ? formData.customer_type
    : (TYPE_FOR_STATUS[formData.status] || 'prospect');

  return (
    <div className="customer-form-page">
      <div className="form-header">
        {/* A new customer started from the list goes back to the list as it
            was left; the other two go back to a page, not a list. */}
        {returnTo || isEdit ? (
          <Link to={returnTo || `/customers/${id}`} className="back-link">
            ← {returnTo ? 'Back' : 'Customer'}
          </Link>
        ) : (
          <BackToList to="/customers" also={['/distributors']} labels={CUSTOMER_LISTS} className="back-link">← Customers</BackToList>
        )}
        <h1>{isEdit ? 'Edit Customer' : 'New Customer'}</h1>
      </div>

      {guardElement}
      <form onChangeCapture={markDirty} onSubmit={handleSubmit} className="customer-form">
        {error && (
          <div className="form-error" ref={errorBannerRef}>
            <span>{error}</span>
            {duplicateCustomer && returnTo && (
              <button
                type="button"
                className="form-error-link"
                onClick={() => navigateBackWith(duplicateCustomer.customer_id, duplicateCustomer.customer_type)}
              >
                Use existing customer
              </button>
            )}
            {duplicateCustomer && !returnTo && (
              <Link to={`/customers/${duplicateCustomer.customer_id}`} className="form-error-link">
                Open existing customer
              </Link>
            )}
            {staleRecord && (
              <button
                type="button"
                className="form-error-link"
                onClick={() => window.location.reload()}
              >
                Reload latest version
              </button>
            )}
          </div>
        )}

        <div className="form-section logo-form-section">
          <input
            type="file"
            ref={logoInputRef}
            onChange={handleLogoChange}
            accept="image/*"
            className="logo-file-input"
          />
          <div className="logo-editor">
            <button
              type="button"
              className={`logo-preview ${uploadingLogo ? 'uploading' : ''}`}
              onClick={handleLogoClick}
              disabled={uploadingLogo}
              aria-label="Upload logo"
            >
              {logoPreview || formData.logo_url ? (
                <img
                  src={logoPreview || `${API_URL}${formData.logo_url}`}
                  alt={`${formData.business_name || 'Customer'} logo`}
                />
              ) : (
                <span>{getInitials(formData.business_name)}</span>
              )}
              {uploadingLogo && <span className="logo-spinner"></span>}
            </button>
            <div className="logo-editor-copy">
              <h2>Customer logo</h2>
              <p>
                Add a farm or distributor logo to replace the initials on the customer record page.
              </p>
              <div className="logo-actions">
                <button type="button" className="btn-logo-upload" onClick={handleLogoClick}>
                  {logoPreview || formData.logo_url ? 'Change logo' : 'Add logo'}
                </button>
                {(logoPreview || formData.logo_url) && (
                  <button type="button" className="btn-logo-remove" onClick={handleRemoveLogo}>
                    Remove
                  </button>
                )}
              </div>
              <span className="logo-help">PNG, JPG, GIF or WebP. Maximum 2MB.</span>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Basic Information</h2>
          <p className="required-legend"><span className="required-star">*</span> Required field</p>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="business_name">Business Name <span className="required-star">*</span></label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="legal_name">Legal Name</label>
              <input
                type="text"
                id="legal_name"
                name="legal_name"
                value={formData.legal_name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="customer_type">Type <span className="required-star">*</span></label>
              <select
                id="customer_type"
                name="customer_type"
                value={formData.customer_type}
                onChange={handleChange}
                required
              >
                {/* Both values are real types: End customer carries the type
                    its status gives, so nothing unknown can be saved. */}
                <option value={endCustomerTypeValue}>End customer</option>
                <option value="distributor">Distributor</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                {/* A record still on a retired status must pick one of the three. */}
                {!CUSTOMER_STATUS_ORDER.includes(formData.status) && (
                  <option value={formData.status} disabled>Choose a status</option>
                )}
                {CUSTOMER_STATUS_ORDER.map(status => (
                  <option key={status} value={status}>{CUSTOMER_STATUS_META[status].label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tier">Tier</label>
              <select
                id="tier"
                name="tier"
                value={formData.tier}
                onChange={handleChange}
              >
                <option value="standard">Standard</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="strategic">Strategic</option>
              </select>
            </div>

            {/* A distributor's health is set by hand. An end customer's is
                worked out by the system each time it is looked at, so a
                value typed here would be replaced straight away. */}
            {formData.customer_type === 'distributor' ? (
              <div className="form-group">
                <label htmlFor="health_score">Health Score</label>
                <select
                  id="health_score"
                  name="health_score"
                  value={formData.health_score}
                  onChange={handleChange}
                >
                  <option value="green">Green - Healthy</option>
                  <option value="amber">Amber - Needs Attention</option>
                  <option value="red">Red - Urgent</option>
                </select>
              </div>
            ) : (
              <div className="form-group">
                <span className="form-label-text">Health Score</span>
                <span className="field-help" data-testid="health-worked-out">
                  Worked out automatically from open tickets, overdue maintenance, robots flagged amber or red, and the last contact with the customer.
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h2>Address</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="address_line_1">Address Line 1</label>
              <input
                type="text"
                id="address_line_1"
                name="address_line_1"
                value={formData.address_line_1}
                onChange={handleChange}
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="address_line_2">Address Line 2</label>
              <input
                type="text"
                id="address_line_2"
                name="address_line_2"
                value={formData.address_line_2}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="town_city">Town/City</label>
              <input
                type="text"
                id="town_city"
                name="town_city"
                value={formData.town_city}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="county_region">County/Region</label>
              <input
                type="text"
                id="county_region"
                name="county_region"
                value={formData.county_region}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="state_province">State/Province</label>
              <input
                type="text"
                id="state_province"
                name="state_province"
                value={formData.state_province}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="postcode">Postcode/Zipcode</label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="country_code">Country</label>
              <CountrySelect
                id="country_code"
                name="country_code"
                value={formData.country_code}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Site facts live on the site form. This flag only decides whether
              that form starts pre-filled with the address above. */}
          {!isEdit && showPrimarySiteSection && (
            <div className="form-group checkbox-group site-same-as-flag">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="site_same_as_business"
                  checked={siteSameAsBusiness}
                  onChange={(e) => setSiteSameAsBusiness(e.target.checked)}
                />
                Site address same as business address
              </label>
              <p className="section-hint">
                Farm details (herd, parlour, milking) are recorded per site —
                you'll be taken to the site form after saving.
              </p>
            </div>
          )}
        </div>

        {!isEdit && (
          <div className="form-section">
            <h2>Primary Contact</h2>
            <p className="section-hint">
              Optional — becomes the first entry on this organisation's contact list.
              Add more contacts from the record's Contacts tab afterwards.
            </p>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="primary_contact_name">Contact Name</label>
                <input
                  type="text"
                  id="primary_contact_name"
                  value={primaryContact.name}
                  onChange={(e) => setPrimaryContact(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="primary_contact_phone">Phone</label>
                <input
                  type="text"
                  id="primary_contact_phone"
                  value={primaryContact.phone}
                  onChange={(e) => setPrimaryContact(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="primary_contact_email">Email</label>
                <input
                  type="email"
                  id="primary_contact_email"
                  value={primaryContact.email}
                  onChange={(e) => setPrimaryContact(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>Commercial</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="source">How did you hear about us?</label>
              <select
                id="source"
                name="source"
                value={formData.source}
                onChange={handleChange}
              >
                <option value="">Select...</option>
                <option value="website">Website</option>
                <option value="social_media">Social Media</option>
                <option value="word_of_mouth">Word of Mouth</option>
                <option value="other">Other</option>
              </select>
            </div>

            {formData.source === 'other' && (
              <div className="form-group">
                <label htmlFor="source_other">Please detail</label>
                <input
                  id="source_other"
                  name="source_other"
                  value={formData.source_other}
                  onChange={handleChange}
                  placeholder="e.g., Met at a farm open day"
                  maxLength={255}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="sale_channel">Sale Channel</label>
              <select
                id="sale_channel"
                name="sale_channel"
                value={formData.sale_channel}
                onChange={handleChange}
              >
                <option value="direct">Direct</option>
                <option value="distributor_full">Distributor (Full)</option>
                <option value="distributor_install_only">Distributor (Install Only)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="commercial_owner">Commercial Owner</label>
              <select
                id="commercial_owner"
                name="commercial_owner"
                value={formData.commercial_owner}
                onChange={handleChange}
              >
                <option value="ptl">PTL</option>
                <option value="distributor">Distributor</option>
              </select>
            </div>

            {/* Only an Active distributor can be newly linked (Gareth 2026-09-14).
                initialLabel still shows an existing link to a prospective or
                dormant one, and that link stays. */}
            {formData.customer_type !== 'distributor' && formData.sale_channel !== 'direct' && (
              <>
                <div className="form-group">
                  <label htmlFor="master_distributor_id">Account Distributor <span className="required-star">*</span></label>
                  <CustomerSearchSelect
                    id="master_distributor_id"
                    kind="distributor"
                    statusFilter="active"
                    value={formData.master_distributor_id}
                    initialLabel={distributorLabels.master}
                    onSelect={handleDistributorSelect('master')}
                    clearable
                  />
                  {renderRestoreDistributor('master')}
                  <span className="field-help">Use this to make the customer indirect and link them to the distributor that owns the account.</span>
                </div>

                <div className="form-group">
                  <label htmlFor="sub_distributor_id">Sub Distributor</label>
                  <CustomerSearchSelect
                    key={`sub-${formData.master_distributor_id}`}
                    id="sub_distributor_id"
                    kind="distributor"
                    statusFilter="active"
                    value={formData.sub_distributor_id}
                    initialLabel={distributorLabels.sub}
                    excludeIds={[formData.master_distributor_id]}
                    placeholder="None - search to add..."
                    onSelect={handleDistributorSelect('sub')}
                    clearable
                  />
                  {renderRestoreDistributor('sub')}
                </div>

                {formData.sale_channel === 'distributor_install_only' && (
                  <div className="form-group">
                    <label htmlFor="install_distributor_id">Install Distributor</label>
                    <CustomerSearchSelect
                      id="install_distributor_id"
                      kind="distributor"
                      statusFilter="active"
                      value={formData.install_distributor_id}
                      initialLabel={distributorLabels.install}
                      placeholder="Use account distributor"
                      onSelect={handleDistributorSelect('install')}
                      clearable
                    />
                    {renderRestoreDistributor('install')}
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label htmlFor="account_owner_id">Account Owner</label>
              <select
                id="account_owner_id"
                name="account_owner_id"
                value={formData.account_owner_id}
                onChange={handleChange}
              >
                <option value="">Unassigned</option>
                {accountOwners.map(owner => (
                  <option key={owner.user_id} value={owner.user_id}>
                    {owner.full_name}{owner.role ? ` (${owner.role})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
              >
                {currencyOptions.map(option => (
                  <option key={option.currency_code} value={option.currency_code}>
                    {option.currency_code} ({option.currency_name})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="payment_terms">Payment Terms</label>
              <select
                id="payment_terms"
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
              >
                <option value="net_30">Net 30</option>
                <option value="net_60">Net 60</option>
                <option value="upfront">Upfront</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {formData.customer_type !== 'distributor' && (
              <>
                <div className="form-group">
                  <label htmlFor="brush_supply_channel">Brush Supply</label>
                  <select
                    id="brush_supply_channel"
                    name="brush_supply_channel"
                    value={formData.brush_supply_channel}
                    onChange={handleChange}
                  >
                    <option value="direct">Direct from IDS</option>
                    <option value="distributor">Via Distributor</option>
                    <option value="none">N/A - no brush supply</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="brush_purchase_cadence">Brush Ordering Cadence</label>
                  <select
                    id="brush_purchase_cadence"
                    name="brush_purchase_cadence"
                    value={formData.brush_purchase_cadence}
                    onChange={handleChange}
                    disabled={formData.brush_supply_channel === 'none'}
                  >
                    <option value="as_required">As required</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="six_monthly">6 monthly</option>
                    <option value="annual">Annually</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="brush_unit_price">Customer Brush Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    id="brush_unit_price"
                    name="brush_unit_price"
                    value={formData.brush_unit_price}
                    onChange={handleChange}
                    placeholder="0.00"
                    disabled={formData.brush_supply_channel === 'none'}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="brush_last_ship_date">Last Brush Ship Date</label>
                  <input
                    type="date"
                    id="brush_last_ship_date"
                    name="brush_last_ship_date"
                    value={formData.brush_last_ship_date}
                    onChange={handleChange}
                    disabled={formData.brush_supply_channel === 'none'}
                  />
                </div>

                {formData.brush_supply_channel === 'distributor' && (
                  <div className="form-group">
                    <label htmlFor="brush_distributor_id">Brush Distributor</label>
                    <CustomerSearchSelect
                      id="brush_distributor_id"
                      kind="distributor"
                      statusFilter="active"
                      value={formData.brush_distributor_id}
                      initialLabel={distributorLabels.brush}
                      placeholder="Use account distributor"
                      onSelect={handleDistributorSelect('brush')}
                      clearable
                    />
                    {renderRestoreDistributor('brush')}
                  </div>
                )}
              </>
            )}

            {formData.customer_type === 'distributor' && (
              <>
                <div className="form-group">
                  <label htmlFor="brush_distributor_price">Distributor Brush Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    id="brush_distributor_price"
                    name="brush_distributor_price"
                    value={formData.brush_distributor_price}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="brush_ordering_agreement">Brush Ordering Agreement</label>
                  <textarea
                    id="brush_ordering_agreement"
                    name="brush_ordering_agreement"
                    value={formData.brush_ordering_agreement}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Ordering cadence, minimum order quantities, delivery terms, or special agreement notes..."
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {isEdit && formData.customer_type !== 'distributor' && (
          <div className="form-section sites-manager-section">
            <div className="section-title-row">
              <div>
                <h2>All Farm Sites</h2>
                <p className="section-hint">Add as many operational sites as this customer needs. Each site can hold its own address, herd, parlour and assets.</p>
              </div>
              <Link to={`/customers/${id}/sites/new`} className="btn-site-add">
                Add Another Site
              </Link>
            </div>

            {farmSites.length === 0 ? (
              <div className="site-manager-empty">
                <span>No farm sites have been added yet.</span>
                <Link to={`/customers/${id}/sites/new`} className="btn-site-add secondary">
                  Add First Site
                </Link>
              </div>
            ) : (
              <div className="site-manager-list">
                {farmSites.map(site => (
                  <div key={site.site_id} className="site-manager-row">
                    <div className="site-manager-main">
                      <div className="site-manager-title">
                        <strong>{site.site_name}</strong>
                        {site.is_primary && <span className="site-pill">Primary</span>}
                      </div>
                      <span>{formatSiteAddress(site) || 'Address not recorded'}</span>
                    </div>
                    <div className="site-manager-meta">
                      <span>{site.cows_milking ? `${site.cows_milking} cows milking` : 'Herd not recorded'}</span>
                      <span>{site.asset_count ? `${site.asset_count} assets` : 'No assets'}</span>
                    </div>
                    <Link to={`/customers/${id}/sites/${site.site_id}/edit`} className="btn-site-edit">
                      Edit Site
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="form-section">
          <h2>Notes</h2>
          <div className="form-group full-width">
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              placeholder="Additional notes about this customer..."
            />
          </div>
        </div>

        <div className="form-actions">
          {returnTo || isEdit ? (
            <Link
              to={returnTo || `/customers/${id}`}
              className="btn-cancel"
            >
              Cancel
            </Link>
          ) : (
            <BackToList to="/customers" also={['/distributors']} className="btn-cancel">Cancel</BackToList>
          )}
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Customer')}
          </button>
        </div>
      </form>

      {/* Forced (Gareth 2026-08-21): the site form is the only way onwards —
          no cancel, no overlay dismiss — so a farm customer can't be left
          siteless by accident. Reuses the ConfirmDialog styling. */}
      {createdCustomerId && (
        <div className="modal-overlay confirm-overlay">
          <div className="modal-content confirm-dialog">
            <div className="confirm-body">
              <h3>Customer saved</h3>
              <p>
                Farm details — herd, parlour, milking — are recorded per site.
                {siteSameAsBusiness
                  ? ' The site form opens next with the business address already filled in.'
                  : ' The site form opens next; enter the site’s own address there.'}
              </p>
            </div>
            <div className="confirm-actions">
              <button type="button" className="confirm-primary" onClick={continueToSiteForm} autoFocus>
                Continue to site form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerForm;
